import { useEditorStore } from '@/lib/state/editorStore';
import { useFicheStore } from '@/lib/state/ficheStore';
import type { Fiche } from '@pharmafiche/schema';
import { listFiches, loadFiche, saveFiche } from './db';

const DEBOUNCE_MS = 500;
let timer: ReturnType<typeof setTimeout> | null = null;
let lastSavedJson: string | null = null;
let started = false;

/**
 * Subscribe to the editor store and debounced-persist fiche changes to
 * IndexedDB. Idempotent — safe to call multiple times.
 */
export function startAutoPersist(): void {
  if (started) return;
  started = true;

  useEditorStore.subscribe((state, prev) => {
    const fiche = state.fiche;
    if (!fiche) return;
    if (prev?.fiche?.id === fiche.id && prev?.fiche === fiche) return;
    const json = JSON.stringify(fiche);
    if (json === lastSavedJson) return;
    lastSavedJson = json;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      saveFiche(fiche).catch((err) => {
        console.warn('autoPersist save failed:', err);
      });
    }, DEBOUNCE_MS);
  });
}

/**
 * Read every fiche from IndexedDB and hydrate ficheStore. Called on app
 * boot so the /library route and /editor/:id can resolve previously saved
 * fiches without going to network.
 */
export async function hydrateFromDb(): Promise<void> {
  const summaries = await listFiches();
  const fiches: Fiche[] = [];
  for (const s of summaries) {
    const f = await loadFiche(s.id);
    if (f) fiches.push(f);
  }
  if (fiches.length > 0) {
    const put = useFicheStore.getState().put;
    for (const f of fiches) put(f);
  }
}
